import axios from 'axios';
import cookieUniversal from 'cookie-universal';
import UAParser from 'ua-parser-js';
import { v4 as uuidv4 } from 'uuid';
import { Logger, LogLevel } from '@tidal/logger';
import { ExperimentRelay } from '../lib/experimentRelay';
import { BaseExperimentRelay } from '../lib/utils/baseExperimentRelay';
import { ExperimentRelayError } from '../lib/utils/experimentRelayError';
export var ClientPlatform;
(function (ClientPlatform) {
    ClientPlatform["Android"] = "android";
    ClientPlatform["CommercialWeb"] = "commercial_web";
    ClientPlatform["Desktop"] = "desktop";
    ClientPlatform["iOS"] = "ios";
    ClientPlatform["Web"] = "web";
})(ClientPlatform || (ClientPlatform = {}));
const EXPERIMENTS_COOKIE_NAME = 'experimentationID';
const EXPERIMENTS_COOKIE_DEFAULT_OPTIONS = {
    maxAge: 365 * 24 * 60 * 60,
    path: '/',
    sameSite: 'lax',
};
export class BaseNuxtExperimentRelay extends BaseExperimentRelay {
    constructor(baseOptions) {
        super();
        this.baseOptions = baseOptions;
        this.api = new ExperimentRelay({
            cdpKey: baseOptions.moduleOptions.cdpKey,
            projectId: baseOptions.moduleOptions.projectId,
            useProductionEndpoint: baseOptions.moduleOptions.useProductionEndpoint,
        });
        this.logger = new Logger({
            logLevel: baseOptions.moduleOptions.logLevel || LogLevel.ERROR,
            prefix: baseOptions.source,
            service: '@tidal/experiment-relay',
        });
    }
    getOrCreateExperimentationID() {
        if (!this.isCookieAllowed('performance')) {
            this.logger.debug('Skipping get or create cookie due to missing consent');
            return null;
        }
        this.logger.debug(`Get or create ${EXPERIMENTS_COOKIE_NAME} cookie`);
        return this.baseOptions.$cookies.get(EXPERIMENTS_COOKIE_NAME) || this.createExperimentationID();
    }
    createExperimentationID() {
        this.logger.debug(`Creating ${EXPERIMENTS_COOKIE_NAME} cookie`);
        const experimentationID = uuidv4();
        const cookieOptions = {
            ...EXPERIMENTS_COOKIE_DEFAULT_OPTIONS,
            ...this.baseOptions.moduleOptions.cookieOptions,
        };
        this.baseOptions.$cookies.set(EXPERIMENTS_COOKIE_NAME, experimentationID, cookieOptions);
        return experimentationID;
    }
    removeExperimentationID() {
        const cookieOptions = {
            ...EXPERIMENTS_COOKIE_DEFAULT_OPTIONS,
            ...this.baseOptions.moduleOptions.cookieOptions,
            maxAge: 0,
        };
        this.baseOptions.$cookies.remove(EXPERIMENTS_COOKIE_NAME, cookieOptions);
    }
    validateUserData(userType, userToken) {
        if (userType === 'ANONYMOUS' && userToken) {
            throw new ExperimentRelayError(new Error('ANONYMOUS experiment type should read token from cookie'));
        }
        if (userType === 'AUTHENTICATED' && !userToken) {
            throw new ExperimentRelayError(new Error('AUTHENTICATED experiment type should pass user id as token'));
        }
    }
    getUserToken(userType, userToken) {
        if (userType === 'ANONYMOUS') {
            const experimentationID = this.getOrCreateExperimentationID();
            if (!experimentationID) {
                throw new ExperimentRelayError(new Error('User token null due to missing consent'));
            }
            return experimentationID;
        }
        if (!userToken) {
            throw new ExperimentRelayError(new Error('AUTHENTICATED experiment type should pass user id as token'));
        }
        return userToken;
    }
    getExperimentRequestsWithToken(experimentRequests) {
        const experimentRequestsWithToken = experimentRequests.map(experiment => {
            this.validateUserData(experiment.userType, experiment.userToken);
            return {
                ...experiment,
                userToken: this.getUserToken(experiment.userType, experiment.userToken),
            };
        });
        if (!this.isNonEmptyArray(experimentRequestsWithToken)) {
            throw new ExperimentRelayError(new Error('Experiment requests should not be empty'));
        }
        return experimentRequestsWithToken;
    }
    isNonEmptyArray(arr) {
        return arr.length > 0;
    }
    getUserAttributes(userAttributes) {
        const userAgentOSName = this.baseOptions.userAgent.os.name?.toLowerCase();
        return {
            ...userAgentOSName && { userAgentOSName },
            ...userAttributes,
        };
    }
    async getExperiments(params) {
        if (!this.isCookieAllowed('performance')) {
            this.logger.debug('Skipping get experiments due to missing consent');
            return null;
        }
        this.logger.debug('Get experiments', params);
        // Assigned first to validate user data
        const experimentRequests = this.getExperimentRequestsWithToken(params.experimentRequests);
        try {
            return await this.api.getExperiments({
                experimentRequests,
                userAttributes: this.getUserAttributes(params.userAttributes),
            });
        }
        catch (error) {
            this.logger.error('Error in get experiments request', error);
            this.captureException(error);
            return null;
        }
    }
    async activateExperiment(params) {
        if (!this.isCookieAllowed('performance')) {
            this.logger.debug('Skipping activate experiment due to missing consent');
            return null;
        }
        this.logger.debug('Activate experiment', params);
        this.validateUserData(params.userType, params.userToken);
        try {
            return await this.api.activateExperiment({
                ...params,
                userAttributes: this.getUserAttributes(params.userAttributes),
                userToken: this.getUserToken(params.userType, params.userToken),
            });
        }
        catch (error) {
            this.logger.error('Error in activate experiment request', error);
            this.captureException(error);
            return null;
        }
    }
    async getAndActivateExperiment(params) {
        if (!this.isCookieAllowed('performance')) {
            this.logger.debug('Skipping get and activate experiment due to missing consent');
            return null;
        }
        const requestParams = {
            ...params,
            userAttributes: this.getUserAttributes(params.userAttributes),
            userToken: this.getUserToken(params.userType, params.userToken),
        };
        this.logger.debug('Get activate experiment - params', requestParams);
        this.validateUserData(params.userType, params.userToken);
        try {
            const response = await this.api.getAndActivateExperiment(requestParams);
            this.logger.debug('Get activate experiment - response', response);
            return response;
        }
        catch (error) {
            this.logger.error('Error in get activate experiment request', error);
            this.captureException(error);
            return null;
        }
    }
    async trackEvent(params) {
        if (!this.isCookieAllowed('performance')) {
            this.logger.debug('Skipping track event due to missing consent');
            return null;
        }
        this.logger.debug('Track event', params);
        this.validateUserData(params.userType, params.userToken);
        try {
            return await this.api.trackEvent({
                ...params,
                userAttributes: this.getUserAttributes(params.userAttributes),
                userToken: this.getUserToken(params.userType, params.userToken),
            });
        }
        catch (error) {
            this.logger.error('Error in track event request', error);
            this.captureException(error);
            return null;
        }
    }
    captureException(error) {
        if (axios.isAxiosError(error)) {
            if (error.response?.status && error.response?.status < 500) {
                this.baseOptions.$sentry.captureException(error);
            }
        }
        else {
            this.baseOptions.$sentry.captureException(error);
        }
    }
}
export class ClientNuxtExperimentRelay extends BaseNuxtExperimentRelay {
    constructor(clientOptions) {
        const ua = new UAParser();
        super({
            $cookies: clientOptions.$cookies,
            $sentry: clientOptions.$sentry,
            moduleOptions: clientOptions.moduleOptions,
            source: 'client',
            userAgent: ua.getResult(),
        });
        this.clientOptions = clientOptions;
    }
    isCookieAllowed(cookieType) {
        return this.clientOptions.$cookieConsent.isCookieAllowed(cookieType);
    }
}
export class ServerNuxtExperimentRelay extends BaseNuxtExperimentRelay {
    constructor(serverOptions) {
        const { req, res, moduleOptions } = serverOptions;
        const cookies = cookieUniversal(req, res);
        const ua = new UAParser(req.headers['user-agent']);
        super({
            $cookies: cookies,
            $sentry: serverOptions.$sentry,
            moduleOptions,
            source: 'server',
            userAgent: ua.getResult(),
        });
        this.serverOptions = serverOptions;
    }
    isCookieAllowed(cookieType) {
        return this.serverOptions.req.$isCookieAllowed(cookieType);
    }
}
